/*!
 * AnderShell - Just a small CSS demo
 *
 * Copyright (c) 2011-2018, Anders Evenrud <andersevenrud@gmail.com>
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are permitted provided that the following conditions are met:
 *
 * 1. Redistributions of source code must retain the above copyright notice, this
 *    list of conditions and the following disclaimer.
 * 2. Redistributions in binary form must reproduce the above copyright notice,
 *    this list of conditions and the following disclaimer in the documentation
 *    and/or other materials provided with the distribution.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS "AS IS" AND
 * ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL THE COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR
 * ANY DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
 * (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER CAUSED AND
 * ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
 * (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF THIS
 * SOFTWARE, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 */
import './src/styles.scss';
import {terminal} from './src/terminal.js';
import {launchRogue} from './src/rogue.js';
import inside68khzUrl from './src/mods/slumgud_-_inside68khz.mod';
import isisUrl from './src/mods/slumgud_-_isis.mod';
import lowFreqOperatorUrl from './src/mods/slumgud_-_low_freq_operator.mod';
import sevenEigthUrl from './src/mods/slumgud_-_seven_eigth.mod';
import sigilOfValhallaUrl from './src/mods/slumgud_-_sigil_of_valhalla.mod';
import speed68Url from './src/mods/slumgud_-_speed68.mod';
import thericanSoundtrackUrl from './src/mods/slumgud_-_therican_soundtrack.mod';
import chiptune2Url from './src/vendor/chiptune2.js';
import libopenmptUrl from './src/vendor/libopenmpt.js';
import libopenmptMemUrl from './src/vendor/libopenmpt.js.mem';

// Banner text
const banner = String.raw`
________  .__                     .__  ________       ___.     
\______ \ |__| ____   ______ ____ |  | \______ \  __ _\_ |__   
 |    |  \|  |/ __ \ /  ___// __ \|  |  |    |  \|  |  \ __ \  
 |   -'   \  \  ___/ \___ \\  ___/|  |__|   -'   \  |  / \_\ \ 
/_______  /__|\___  >____  >\___  >____/_______  /____/|_____/ 
        \/        \/     \/     \/             \/              
 
SYSOP: SLUMGUD
WELCOME TO DIESELDUB.ORG

Type "help" for commands.
`;

// Help text
const helpText = `
Available commands:

help - This output
contact - Prints contact information
music on - Starts the selected module
music off - Stops music
music <file.mod> - Selects and starts a module
dl <file.mod> - Downloads a module
rogue - Launches ASCII Rogue
clear - Clears the display
ls - Lists files
pwd - Lists current directory
cd <dir> - Enters directory
cat <filename> - Lists file contents
`;

// Contact texts
const contactInfo = {
  email: 'post@dieseldub.com',
  phone: '+47 9866 0288',	
};

const contactList = Object.keys(contactInfo)
  .reduce((result, key) => result.concat([`${key} - ${contactInfo[key]}`]), [])
  .join('\n');

const contactText = `

${contactList}

Use ex. 'contact email' to open the links.
`;

const musicFiles = {
  'slumgud_-_inside68khz.mod': inside68khzUrl,
  'slumgud_-_isis.mod': isisUrl,
  'slumgud_-_low_freq_operator.mod': lowFreqOperatorUrl,
  'slumgud_-_seven_eigth.mod': sevenEigthUrl,
  'slumgud_-_sigil_of_valhalla.mod': sigilOfValhallaUrl,
  'slumgud_-_speed68.mod': speed68Url,
  'slumgud_-_therican_soundtrack.mod': thericanSoundtrackUrl
};

let selectedModule = 'slumgud_-_sigil_of_valhalla.mod';
let musicPlayer;
let loadedModule;
let loadingModule;
let musicRuntimePromise;

const loadScript = src => new Promise((resolve, reject) => {
  const script = document.createElement('script');
  script.src = src;
  script.onload = resolve;
  script.onerror = reject;
  document.head.appendChild(script);
});

const ensureMusicRuntime = () => {
  if (typeof window.ChiptuneJsPlayer !== 'undefined' &&
      typeof window.ChiptuneJsConfig !== 'undefined') {
    return Promise.resolve();
  }

  if (!musicRuntimePromise) {
    window.libopenmpt = window.libopenmpt || {};
    window.libopenmpt.locateFile = () => libopenmptMemUrl;
    musicRuntimePromise = loadScript(libopenmptUrl)
      .then(() => loadScript(chiptune2Url));
  }

  return musicRuntimePromise;
};

const initMusicPlayer = () => {
  if (!musicPlayer && typeof window.ChiptuneJsPlayer !== 'undefined' &&
      typeof window.ChiptuneJsConfig !== 'undefined') {
    musicPlayer = new window.ChiptuneJsPlayer(
      new window.ChiptuneJsConfig(-1, 2, 3)
    );
  }
};

const stopMusic = () => {
  if (musicPlayer && typeof musicPlayer.stop === 'function') musicPlayer.stop();
};

const startMusic = filename => {
  const url = musicFiles[filename];

  if (!url) return `Module '${filename}' not found. Use cd music and ls.`;

  selectedModule = filename;
  if (loadingModule === filename) return `Loading ${filename}...`;
  loadingModule = filename;

  ensureMusicRuntime().then(() => {
    initMusicPlayer();
    if (!musicPlayer) throw new Error('Music player failed to initialize');

    if (loadedModule && loadedModule.filename === filename) {
      musicPlayer.play(loadedModule.buffer);
      return;
    }

    musicPlayer.load(url, buffer => {
      loadedModule = {filename, buffer};
      musicPlayer.play(buffer);
    });
  }).catch(error => {
    // Keep the terminal usable if the decoder or audio context is unavailable.
    console.error(error);
  }).then(() => {
    loadingModule = undefined;
  });

  return `Loading ${filename}...`;
};

const musicCommand = arg => {
  const command = (arg || '').toLowerCase();
  if (command === 'off') {
    stopMusic();
    return 'Music stopped.';
  }
  return startMusic(command === 'on' || command === '' ? selectedModule : command);
};

const downloadModule = filename => {
  const normalized = (filename || '').toLowerCase();
  const url = musicFiles[normalized];
  if (!url) return `Module '${filename}' not found.`;

  const link = document.createElement('a');
  link.href = url;
  link.download = normalized;
  link.click();
  return `Downloading ${normalized}...`;
};

const openContact = key => window.open(key === 'email'
  ? `mailto:${contactInfo[key]}`
  : contactInfo[key]);

// File browser
const browser = (function() {
  let current = '/';

  let tree = [{
    location: '/',
    filename: 'Games',
    type: 'directory'
  }, {
    location: '/Games',
    filename: 'Rogue',
    type: 'directory'
  }, {
    location: '/Games/Rogue',
    filename: 'rogue.exe',
    type: 'file',
    content: 'DIESELDUB WEB EXECUTABLE\n\nRun the game with: rogue'
  }, {
    location: '/Games/Rogue',
    filename: 'help.txt',
    type: 'file',
    content: 'DIESELDUB ROGUE\n\nThis is the browser-native Rogue 5.4.4 port.\n\nStart from the BBS prompt with: rogue\n\nMovement: y k u / h . l / b j n\nArrow keys and numpad are also supported.\nQ then Y quits the game.'
  }, {
    location: '/',
    filename: 'music',
    type: 'directory'
  }, ...Object.keys(musicFiles).map(filename => ({
    location: '/music',
    filename,
    type: 'file',
    content: 'MOD tracker module'
  }))];

  const fix = str => str.trim().replace(/\/+/g, '/') || '/';

  const setCurrent = dir => {
    if (typeof dir !== 'undefined') {
      if (dir == '..') {
        const parts = current.split('/');
        parts.pop();
        current = fix(parts.join('/'));
      } else {
        const requested = fix(dir).toLowerCase();
        const found = tree.filter(iter => iter.location === current)
          .find(iter => iter.filename.toLowerCase() === requested);

        if (found) {
          current = fix(current + '/' + found.filename);
        } else {
          return `Directory '${dir}' not found in '${current}'`;
        }
      }

      return `Entered '${current}'`;
    }

    return current;
  };

  const ls = () => {
    const found = tree.filter(iter => iter.location === current);
    const fileCount = found.filter(iter => iter.type === 'file').length;
    const directoryCount = found.filter(iter => iter.type === 'directory').length;
    const status = `${fileCount} file(s), ${directoryCount} dir(s)`;
    const maxlen = Math.max(...found.map(iter => iter.filename).map(n => n.length));

    const list = found.map(iter => {
      return `${iter.filename.padEnd(maxlen + 1, ' ')} <${iter.type}>`;
    }).join('\n');

    return `${list}\n\n${status} in ${current}`;
  };

  const cat = filename => {
    const found = tree.filter(iter => iter.location === current);
    const foundFile = found.find(iter => iter.filename.toLowerCase() === String(filename).toLowerCase());

    if (foundFile) {
      return foundFile.content;
    }

    return `File '${filename}' not found in '${current}'`;
  };

  return {
    cwd: () => setCurrent(),
    cd: dir => setCurrent(fix(dir)),
    cat,
    ls
  };
})();

///////////////////////////////////////////////////////////////////////////////
// MAIN
///////////////////////////////////////////////////////////////////////////////

const load = () => {
  const t = terminal({
    prompt: () => `$ ${browser.cwd()} > `,
    banner,
    commands: {
      help: () => helpText,
      cwd: () => browser.cwd(),
      cd: dir => browser.cd(dir),
      ls: () => browser.ls(),
      cat: file => browser.cat(file),
      dl: file => downloadModule(file),
      music: musicCommand,
      rogue: () => {
        launchRogue(t);
        return 'Launching DUNGEONS OF DOOM...';
      },
      clear: () => t.clear(),
      contact: (key) => {
        if (key in contactInfo) {
          openContact(key);
          return `Opening ${key} - ${contactInfo[key]}`;
        }

        return contactText;
      }
    }
  });
};

document.addEventListener('DOMContentLoaded', load);
